require('./style.scss');

// English fallbacks for the tooltip keys the drawer re-uses as item labels.
// translate() returns the key itself when the remote vocabulary has no entry,
// so every label needs a readable default.
const DEFAULT_LABELS = {
    TOOLTIP_FULLSCREEN: 'Full Screen',
    TOOLTIP_SHARE: 'Share',
    TOOLTIP_ACCESSIBILITY: 'Accessibility',
    TOOLTIP_E3LIVE: 'Shared Viewing',
    TOOLTIP_E3LIVE_PLUS: 'Invite',
    TOOLTIP_E3LIVE_CANCEL: 'End Call',
    TOOLTIP_E3LIVE_SCREEN_SHARE: 'Share Screen',
    TOOLTIP_E3TOUR_HELP: 'Help',
    TOOLTIP_E3TOUR_INFO: 'Tour Info',
    TOOLTIP_GYRO: 'Gyroscope',
    TOOLTIP_VR: 'VR',
    TOOLTIP_MEASUREMENT: 'Measurement',
    TOOLTIP_LEAD_GEN: 'Contact Us',
    TOOLTIP_E3PLAY: 'Auto Tour',
    TOOLTIP_SCENES: 'Scenes',
    TOOLTIP_2D_FLOORPLAN: '2D Floor Plan',
    TOOLTIP_3D_FLOORPLAN: '3D Floor Plan',
    MENU_TITLE: 'Menu'
};

const ANCHORS = ['left', 'right'];
const CLASS_PREFIX = 'e3-menu-drawer';

/**
 * Temporary navigation drawer for the viewer control bar.
 *
 * It does not build its own buttons — it hosts the very same
 * `<button class="control control-*">` elements the panel already creates, so
 * every existing listener, `.active`/`.hidden` toggle and tooltip registration
 * keeps working after a control moves in here. The list carries the
 * `pannellum-panel-controls` class purely so the control icon rules
 * (`.pannellum-panel-controls .control-share:before`, …) still match.
 */
export default class E3MenuDrawer {
    constructor(container, {translationService, anchor = 'left', titleKey = 'MENU_TITLE', onOpen, onClose} = {}) {
        this._container = container;
        this._anchor = ANCHORS.indexOf(anchor) !== -1 ? anchor : 'left';
        this._titleKey = titleKey;
        this._onOpen = onOpen;
        this._onClose = onClose;
        this._isOpen = false;
        this._lastFocused = null;

        this._translate = translationService
            ? translationService.translate.bind(translationService)
            : (key) => key;
        this._translationPromise = translationService
            ? translationService.getTranslation()
            : Promise.resolve();

        this._onKeyDown = this._handleKeyDown.bind(this);
        this._onItemClick = this.close.bind(this);

        this._build();
    }

    get list() {
        return this._list;
    }

    get isOpen() {
        return this._isOpen;
    }

    /**
     * Number of items that are currently visible — controls hidden with the
     * `hidden` class (live-session buttons, disabled features) do not count.
     */
    get size() {
        return Array.from(this._list.children)
            .filter((el) => !el.classList.contains('hidden'))
            .length;
    }

    isEmpty() {
        return this.size === 0;
    }

    /**
     * Decorate a control button that has just been appended to the list:
     * adds the text label next to the icon and closes the drawer on activation.
     */
    addControl(button, titleKey) {
        button.classList.add(`${CLASS_PREFIX}__item`);

        let label = button.querySelector(`.${CLASS_PREFIX}__label`);
        if (!label) {
            label = document.createElement('span');
            label.className = `${CLASS_PREFIX}__label`;
            button.appendChild(label);
        }

        const fallback = DEFAULT_LABELS[titleKey] || '';
        label.textContent = fallback;

        if (titleKey) {
            this._translationPromise
                .then(() => {
                    label.textContent = this._t(titleKey, fallback);
                })
                .catch(() => {});
        }

        if (!button.dataset.drawerBound) {
            button.dataset.drawerBound = '1';
            button.addEventListener('click', this._onItemClick);
        }
    }

    removeControl(button) {
        if (!button) return;

        const label = button.querySelector(`.${CLASS_PREFIX}__label`);
        if (label) button.removeChild(label);

        button.classList.remove(`${CLASS_PREFIX}__item`);
        button.removeEventListener('click', this._onItemClick);
        delete button.dataset.drawerBound;
    }

    open() {
        if (this._isOpen) return;

        this._isOpen = true;
        this._lastFocused = document.activeElement;
        this._root.classList.add('is-open');
        this._root.setAttribute('aria-hidden', 'false');
        document.addEventListener('keydown', this._onKeyDown, true);

        const first = this._list.querySelector('.control:not(.hidden):not(:disabled)');
        if (first) first.focus();

        if (this._onOpen) this._onOpen();
    }

    close() {
        if (!this._isOpen) return;

        this._isOpen = false;
        this._root.classList.remove('is-open');
        this._root.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', this._onKeyDown, true);

        if (this._lastFocused && this._lastFocused.focus) this._lastFocused.focus();
        this._lastFocused = null;

        if (this._onClose) this._onClose();
    }

    toggle() {
        this._isOpen ? this.close() : this.open();
    }

    destroy() {
        document.removeEventListener('keydown', this._onKeyDown, true);

        Array.from(this._list.children).forEach((button) => this.removeControl(button));

        if (this._root.parentNode) this._root.parentNode.removeChild(this._root);

        this._root = this._list = this._container = null;
    }

    _t(key, fallback) {
        const value = this._translate(key);

        return !value || value === key ? (fallback || key) : value;
    }

    _build() {
        this._root = document.createElement('div');
        this._root.className = `${CLASS_PREFIX} ${CLASS_PREFIX}--${this._anchor}`;
        this._root.setAttribute('aria-hidden', 'true');

        const backdrop = document.createElement('div');
        backdrop.className = `${CLASS_PREFIX}__backdrop`;
        backdrop.addEventListener('click', () => this.close());
        this._root.appendChild(backdrop);

        const paper = document.createElement('div');
        paper.className = `${CLASS_PREFIX}__paper`;
        paper.setAttribute('role', 'menu');

        const header = document.createElement('div');
        header.className = `${CLASS_PREFIX}__header`;

        const title = document.createElement('span');
        title.className = `${CLASS_PREFIX}__title`;
        title.textContent = DEFAULT_LABELS[this._titleKey] || '';
        this._translationPromise
            .then(() => {
                title.textContent = this._t(this._titleKey, DEFAULT_LABELS[this._titleKey]);
            })
            .catch(() => {});
        header.appendChild(title);

        const close = document.createElement('button');
        close.type = 'button';
        close.className = `${CLASS_PREFIX}__close`;
        close.setAttribute('aria-label', 'Close');
        close.innerHTML = '&times;';
        close.addEventListener('click', () => this.close());
        header.appendChild(close);

        paper.appendChild(header);

        // `pannellum-panel-controls` keeps the existing control icon rules matching.
        this._list = document.createElement('div');
        this._list.className = `${CLASS_PREFIX}__list pannellum-panel-controls`;
        paper.appendChild(this._list);

        this._root.appendChild(paper);
        this._container.appendChild(this._root);
    }

    _handleKeyDown(e) {
        if (!this._isOpen) return;

        if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
            e.stopPropagation();
            this.close();
        }
    }
}
